import { CSSProperties } from 'react';
import { MorphSpec } from '../../../types/appearance';
import { MorphIcon, RegionGlyph } from './MorphIcon';
import { ResetIcon } from './EditorIcons';
import './MorphSlider.css';

interface MorphSliderProps {
  spec: MorphSpec;
  value: number;
  onChange: (value: number) => void;
}

/** Human-friendly value: physical Δ% for scale morphs (default 1.0), else
 * position % across the range ("how far toward max"). Raw floats read like a
 * spreadsheet; these read like a body ("+8%", "35%"). */
function formatValue(spec: MorphSpec, value: number): { text: string; signed: boolean } {
  if (spec.default === 1) {
    const pct = Math.round((value - 1) * 100);
    return { text: `${pct > 0 ? '+' : ''}${pct}%`, signed: true };
  }
  const pct = Math.round(((value - spec.min) / (spec.max - spec.min)) * 100);
  return { text: `${pct}%`, signed: false };
}

export function MorphSlider({ spec, value, onChange }: MorphSliderProps) {
  const isDefault = Math.abs(value - spec.default) < 0.005;
  const range = spec.max - spec.min;
  const pos = ((value - spec.min) / range) * 100;
  const notch = ((spec.default - spec.min) / range) * 100;
  const { text: valueText } = formatValue(spec, value);

  // The gauge fills from the neutral notch toward the thumb (either direction),
  // driven by CSS vars so the track pseudo-elements can paint it.
  const gaugeVars = {
    '--morph-pos': `${pos}%`,
    '--morph-fill-lo': `${Math.min(pos, notch)}%`,
    '--morph-fill-hi': `${Math.max(pos, notch)}%`,
  } as CSSProperties;

  return (
    <div className={`morph-slider ${isDefault ? '' : 'morph-modified'}`}>
      <div className="morph-icon-tile" title={spec.label}>
        <MorphIcon morphKey={spec.key} />
      </div>
      <div className="morph-main">
        <div className="morph-slider-header">
          <label className="control-label" htmlFor={`morph-${spec.key}`}>
            {spec.label}
          </label>
          <span className={`morph-value-chip ${isDefault ? 'neutral' : 'modified'}`}>
            {valueText}
          </span>
          <button
            type="button"
            className="morph-reset-btn"
            onClick={() => onChange(spec.default)}
            disabled={isDefault}
            title={`Reset ${spec.label.toLowerCase()}`}
            aria-label={`Reset ${spec.label.toLowerCase()}`}
          >
            <ResetIcon size={13} />
          </button>
        </div>
        <div className="morph-gauge-row">
          <RegionGlyph morphKey={spec.key} end="min" />
          <div className="morph-gauge" style={gaugeVars}>
            <span className="morph-notch" style={{ left: `${notch}%` }} aria-hidden="true" />
            <input
              id={`morph-${spec.key}`}
              type="range"
              className="morph-range"
              min={spec.min}
              max={spec.max}
              step={spec.step}
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
            />
          </div>
          <RegionGlyph morphKey={spec.key} end="max" />
        </div>
      </div>
    </div>
  );
}

export default MorphSlider;
